// "Your email is not being delivered" — the banner that makes a FAILING
// transport impossible to miss.
//
// Health is written by the send-time reporter and the 05:00 IST cron (plan §6);
// this only renders it, and offers the one action that can clear it: send a
// real test mail, which feeds the same state machine. Nothing renders while the
// transport is OK — a steady state does not need a banner.

import { useState } from 'react';
import { Button, useToast } from '@bbpos/ui';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/constants/design-tokens';
import type { EmailTransportClient } from '@/services/email-transport.service';
import type { EmailTransportConfigMasked } from '@bbpos/types';

function AlertIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

function formatStamp(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
}

export function EmailHealthBanner({
  config,
  client,
  canEdit,
  onRechecked,
}: {
  config: EmailTransportConfigMasked | null;
  client: EmailTransportClient;
  canEdit: boolean;
  onRechecked?: () => void | Promise<void>;
}) {
  const { toast } = useToast();
  const [rechecking, setRechecking] = useState(false);

  if (!config || config.health_status !== 'FAILING') return null;

  const checkedAt = formatStamp(config.health_checked_at);

  const handleRecheck = async () => {
    setRechecking(true);
    try {
      const result = await client.sendTest();
      if (result.ok) toast({ variant: 'success', title: 'Delivery is working again' });
      else toast({ variant: 'warning', title: 'Still failing', description: result.error });
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Re-check failed',
        description: err instanceof ApiError ? err.message : 'Could not reach the mail server',
      });
    } finally {
      setRechecking(false);
      await onRechecked?.();
    }
  };

  return (
    <div
      role="alert"
      data-testid="email-health-banner"
      className={cn(
        'animate-fade-in rounded-xl border border-danger-200 bg-gradient-to-r from-danger-50 to-warning-50 p-5 shadow-md',
        'dark:border-danger-800 dark:from-danger-900/30 dark:to-warning-900/20',
      )}
    >
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-danger-500 to-warning-500 text-white shadow-sm">
          <AlertIcon />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-danger-800 dark:text-danger-200">
            Email delivery is failing
          </p>
          <p className="mt-0.5 text-xs text-danger-700 dark:text-danger-300">
            Orio360 could not send through <span className="font-semibold">{config.email}</span>.
            Invoices and notifications are not reaching your customers until this is fixed.
          </p>
          {config.health_error && (
            <p className="mt-2 break-words rounded-lg bg-white/70 px-3 py-2 font-mono text-xs text-danger-800 dark:bg-neutral-900/50 dark:text-danger-200">
              {config.health_error}
            </p>
          )}
          <p className="mt-2 text-xs text-danger-600 dark:text-danger-400">
            {checkedAt ? `Last checked ${checkedAt}` : 'Not checked yet'}
            {config.consecutive_failures > 0 && ` · ${config.consecutive_failures} failed in a row`}
          </p>
        </div>
        {canEdit && (
          <Button
            variant="danger"
            size="sm"
            loading={rechecking}
            onClick={() => void handleRecheck()}
          >
            Re-check
          </Button>
        )}
      </div>
    </div>
  );
}
